import type { OrderType } from '@alphabet-hunter/shared';
import { AppScreen } from './AppScreen';

interface SetupScreenProps {
  orderType: OrderType;
  onOrderTypeChange: (value: OrderType) => void;
  onStart: () => void;
  onBack: () => void;
}

export function SetupScreen({ orderType, onOrderTypeChange, onStart, onBack }: SetupScreenProps) {
  return (
    <AppScreen>
      <div className="sticky top-0 z-10 bg-atlas-bg dark:bg-atlas-bg-dark shrink-0 flex items-center justify-between pt-[calc(env(safe-area-inset-top)+14px)] pb-1.5 px-4">
        <button
          onClick={onBack}
          aria-label="Back"
          className="min-h-[44px] min-w-[44px] flex items-center justify-center text-atlas-ink dark:text-atlas-ink-dark"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <span className="text-xs font-semibold uppercase tracking-wide text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
          Game Setup
        </span>
        <div className="w-[44px]" />
      </div>

      <div className="flex-1 flex flex-col px-6 py-6 gap-4">
        <div className="bg-atlas-surface dark:bg-atlas-surface-dark rounded-2xl p-4 shadow-sm">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-atlas-ink-soft dark:text-atlas-ink-soft-dark mb-3">
            Letter Order
          </h3>
          <div className="flex flex-col gap-2.5">
            <label
              className={`flex items-center gap-3.5 px-4 py-3.5 rounded-xl border cursor-pointer transition-colors ${
                orderType === 'sequential'
                  ? 'border-atlas-brass dark:border-atlas-brass-dark bg-atlas-brass/10 dark:bg-atlas-brass-dark/15'
                  : 'border-atlas-rule dark:border-atlas-rule-dark'
              }`}
            >
              <input
                type="radio"
                name="letter-order"
                value="sequential"
                checked={orderType === 'sequential'}
                onChange={() => onOrderTypeChange('sequential')}
                className="w-5 h-5 accent-atlas-brass dark:accent-atlas-brass-dark shrink-0"
              />
              <div className="flex flex-col gap-0.5">
                <span className="font-semibold text-[15px] text-atlas-ink dark:text-atlas-ink-dark">A → Z Sequential</span>
                <span className="text-xs text-atlas-ink-soft dark:text-atlas-ink-soft-dark">Go through the alphabet in order</span>
              </div>
            </label>
            <label
              className={`flex items-center gap-3.5 px-4 py-3.5 rounded-xl border cursor-pointer transition-colors ${
                orderType === 'random'
                  ? 'border-atlas-brass dark:border-atlas-brass-dark bg-atlas-brass/10 dark:bg-atlas-brass-dark/15'
                  : 'border-atlas-rule dark:border-atlas-rule-dark'
              }`}
            >
              <input
                type="radio"
                name="letter-order"
                value="random"
                checked={orderType === 'random'}
                onChange={() => onOrderTypeChange('random')}
                className="w-5 h-5 accent-atlas-brass dark:accent-atlas-brass-dark shrink-0"
              />
              <div className="flex flex-col gap-0.5">
                <span className="font-semibold text-[15px] text-atlas-ink dark:text-atlas-ink-dark">Random Order</span>
                <span className="text-xs text-atlas-ink-soft dark:text-atlas-ink-soft-dark">Surprise! Letters in random sequence</span>
              </div>
            </label>
          </div>
        </div>

        <div className="bg-atlas-surface dark:bg-atlas-surface-dark rounded-2xl p-4 border-l-4 border-atlas-brass dark:border-atlas-brass-dark">
          <p className="text-atlas-ink-soft dark:text-atlas-ink-soft-dark text-sm leading-relaxed">
            Scan road signs and be the first to spot a word starting with each letter!
          </p>
        </div>

        <button
          onClick={onStart}
          className="w-full h-[52px] rounded-xl bg-atlas-brass dark:bg-atlas-brass-dark hover:brightness-95 active:brightness-90 text-atlas-ink font-semibold text-[17px] transition-all mt-auto"
        >
          Start Game
        </button>
      </div>
    </AppScreen>
  );
}
